import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Modal from '@mui/material/Modal';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import axios from 'axios';
import { streamElasticAILogs } from '../../../utils/Api';

// Define sources array
const sources = [
  { icon: "🔥", label: "Firewalls" },
  { icon: "🛡️", label: "EDR/XDR" },
  { icon: "☁️", label: "Cloud" },
  { icon: "📊", label: "SIEM Agents" },
  { icon: "💻", label: "Applications" },
  { icon: "🌐", label: "Network" },
  { icon: "🔐", label: "Identity" },
  { icon: "📧", label: "Email" },
];

const LiveEventPath3Splunk = () => {
    const [events, setEvents] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const eventSourceRef = useRef(null);

    // Function to parse streaming log data into display format
    const parseLogData = (rawLog) => {
      try {
        // Parse the JSON string if needed
        const logData = typeof rawLog === 'string' ? JSON.parse(rawLog) : rawLog;
        
        // The actual log data is in logData.source.message as a JSON string
        const sourceMessage = JSON.parse(logData.source.message);
        
        // Use sourceMessage instead of parsed
        const parsed = sourceMessage;
        
        // Extract event severity/level for display
        const getSeverityLevel = (level) => {
          const severityMap = {
            0: 'EMERGENCY',
            1: 'ALERT', 
            2: 'CRITICAL',
            3: 'ERROR',
            4: 'WARNING',
            5: 'NOTICE',
            6: 'INFO',
            7: 'DEBUG'
          };
          return severityMap[level] || 'INFO';
        };

        const result = {
          id: logData.id || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          host: parsed.host || 'unknown',
          level: getSeverityLevel(parsed.level),
          levelNum: parsed.level || 6,
          message: parsed.originalLog || 'No raw message',
          remoteAddress: parsed.remoteAddress || 'N/A',
          attackPattern: parsed.attackPattern || 'N/A',
          explanation: parsed.explanation || 'N/A',
          mitreAttackTechnique: parsed.mitreAttackTechnique || 'N/A',
          remediationSteps: parsed.remediationSteps || 'N/A',
          similarAttacks: parsed.similarAttacks || 'N/A',
          suspiciousTraffic: parsed.suspiciousTraffic || 'N/A',
          threatCategory: parsed.threatCategory || 'N/A',
          severity: parsed.severity || 'N/A',
          aiStatus: parsed.aiStatus !== undefined ? parsed.aiStatus : 'N/A',
          extractedIOCs: parsed.extractedIOCs?.ips || [],
          llm_kill_chain_stage: parsed.llm_kill_chain_stage || 'N/A',
          llm_attack_narrative: parsed.llm_attack_narrative || 'N/A',
          llm_decoded_commands: parsed.llm_decoded_commands || 'N/A',
          llm_data_classification: parsed.llm_data_classification || 'N/A',
          llm_user_risk_score: parsed.llm_user_risk_score || 'N/A',
          llm_asset_criticality: parsed.llm_asset_criticality || 'N/A',
          llm_false_positive_likelihood: parsed.llm_false_positive_likelihood || 'N/A',
          llm_context_window: parsed.llm_context_window || 'N/A',
          llm_anomaly_score: parsed.llm_anomaly_score || 'N/A',
          llm_recommended_actions: parsed.llm_recommended_actions || 'N/A',
          confidenceScore: parsed.confidenceScore || 'N/A'
        };

        console.log('Parsed log event:', result);
        
        
        return result;
      } catch (error) {
        console.error('Error parsing log data:', error);
        console.error('Raw log that caused error:', rawLog);
        
        // Try to create a fallback event with whatever data we have
        let fallbackMessage = 'Unparseable log data';
        let fallbackHost = 'unknown';
        
        try {
          // If it's a simple string, use it directly
          if (typeof rawLog === 'string') {
            // Try to extract any useful info from the string
            fallbackMessage = rawLog.length > 100 ? rawLog.substring(0, 100) + '...' : rawLog;
          } else if (rawLog && typeof rawLog === 'object') {
            // If it's an object, try to stringify it
            fallbackMessage = JSON.stringify(rawLog).substring(0, 100) + '...';
          }
        } catch (fallbackError) {
          console.error('Even fallback parsing failed:', fallbackError);
        }
        
        // Return a fallback event if parsing fails
        return {
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          host: fallbackHost,
          level: 'ERROR',
          levelNum: 3,
          message: fallbackMessage,
          remoteAddress: 'N/A',
          attackPattern: 'parsing_error',
          explanation: 'Failed to parse incoming log data',
          mitreAttackTechnique: 'N/A',
          remediationSteps: 'Check log format and parsing logic',
          similarAttacks: 'N/A',
          suspiciousTraffic: 'N/A',
          threatCategory: 'parsing_error',
          severity: 'N/A',
          aiStatus: 'N/A',
          extractedIOCs: [],
          llm_kill_chain_stage: 'N/A',
          llm_attack_narrative: 'N/A',
          llm_decoded_commands: 'N/A',
          llm_data_classification: 'N/A',
          llm_user_risk_score: 'N/A',
          llm_asset_criticality: 'N/A',
          llm_false_positive_likelihood: 'N/A',
          llm_context_window: 'N/A',
          llm_anomaly_score: 'N/A',
          llm_recommended_actions: 'N/A',
          confidenceScore: 'N/A'
        };
      }
    };

    // Stream Elastic logs using Server-Sent Events or WebSocket
    useEffect(() => {
      const connectToStream = async () => {
        try {
          setIsConnected(true);
          
          // Option 1: Server-Sent Events (SSE)
          if (typeof EventSource !== 'undefined') {
            eventSourceRef.current = new EventSource(streamElasticAILogs);
            
            eventSourceRef.current.onopen = () => {
              setIsConnected(true);
            };
            
            eventSourceRef.current.onmessage = (event) => {
              
              try {
                // Parse the incoming data to check what type it is
                const rawData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                
                // Filter out connection/status messages - only process actual log data
                if (rawData.message && rawData.message.includes('Connected to')) {
                  return; // Skip processing connection messages
                }
                
                // Check if this looks like actual log data (should have source.message structure)
                if (!rawData.source || !rawData.source.message) {
                  console.warn('Received data that is not a log entry:', rawData);
                  return; // Skip processing non-log data
                }
                
                const parsedEvent = parseLogData(event.data);
                
                setEvents(prevEvents => {
                  const newEvents = [parsedEvent, ...prevEvents].slice(0, 20);
                  return newEvents;
                });
              } catch (parseError) {
                console.error('Failed to parse SSE event:', parseError);
                console.error('Event data that failed:', event.data);
              }
            };
            
            eventSourceRef.current.onerror = (error) => {
              console.error('Stream connection error:', error);
              setIsConnected(false);
            };
          } else {
            // Fallback: Polling method if SSE is not supported
            const pollInterval = setInterval(async () => {
              try {
                const response = await axios.get(streamElasticFilterLogs);
                if (response.data) {
                  const parsedEvent = parseLogData(response.data);
                  setEvents(prevEvents => {
                    const newEvents = [parsedEvent, ...prevEvents].slice(0, 20);
                    return newEvents;
                  });
                }
              } catch (error) {
                console.error('Error polling elastic logs:', error);
              }
            }, 2000); // Poll every 2 seconds
            
            return () => clearInterval(pollInterval);
          }
          
        } catch (error) {
          console.error('Error connecting to stream:', error);
          setIsConnected(false);
        }
      };

      connectToStream();

      // Cleanup function
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }
        setIsConnected(false);
      };
    }, []);

  return (
    <>
        <CardContent sx={{ maxHeight: 620, overflowY: 'auto', p: 2, pb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#60a5fa' }}>Live Splunk Logs</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                </Box>
              </Box>
              <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
              {/* Header Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, fontWeight: 700, color: '#b0bec5', mb: 1, position: 'sticky', top: 0, background: 'rgba(10,34,64,0.92)', zIndex: 2, py: 1 }}>
                <Typography variant="caption" sx={{ width: 120, minWidth: 120, maxWidth: 120 }}>Host</Typography>
                <Typography variant="caption" sx={{ flex: 1, minWidth: 0 }}>Raw Message</Typography>
                <Typography variant="caption" sx={{ width: 140, minWidth: 140, maxWidth: 140, textAlign: 'center' }}>Remote Address</Typography>
                <Typography variant="caption" sx={{ width: 80, minWidth: 80, maxWidth: 80, textAlign: 'center' }}>AI Analysis</Typography>
              </Box>
              {events.length === 0 ? (
                <Box sx={{ textAlign: 'center', mt: 4, p: 3 }}>
                  <Typography variant="body2" sx={{ color: '#b0bec5', mb: 1 }}>
                    {isConnected ? 'Waiting for log events...' : 'No connection to log stream'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    {isConnected ? 'Stream is active and ready to receive data' : 'Check your API connection'}
                  </Typography>
                </Box>
              ) : (
                events.map((row, i) => {
                  // Get severity color
                  const getSeverityColor = (levelNum) => {
                    if (levelNum <= 2) return '#f44336'; // Critical/Alert - Red
                    if (levelNum <= 4) return '#ff9800'; // Error/Warning - Orange  
                    if (levelNum <= 5) return '#2196f3'; // Notice - Blue
                    return '#4caf50'; // Info/Debug - Green
                  };
                  
                  const severityColor = getSeverityColor(row.levelNum);
                  
                  return (
                    <Box key={`${row.id}_${i}`} sx={{ 
                      mb: 1.5, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      flexWrap: 'wrap',
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(96,165,250,0.1)',
                        transition: 'background-color 0.2s'
                      }
                    }}>
                      
                      
                      {/* Host */}
                      <Typography 
                        variant="body2" 
                        title={row.host} 
                        sx={{ 
                          color: '#60a5fa', 
                          width: 120,
                          minWidth: 120,
                          maxWidth: 120,
                          fontWeight: 600,
                          fontFamily: 'monospace',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {row.host}
                      </Typography>
                      
                      
                      
                      {/* Message */}
                      <Typography 
                        variant="body2" 
                        title={row.message}
                        sx={{ 
                          flex: 1, 
                          minWidth: 0,
                          color: 'white', 
                          fontWeight: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'help'
                        }}
                      >
                        {row.message}
                      </Typography>
                      
                      {/* Remote Address */}
                      <Typography 
                        variant="caption" 
                        title={`Remote IP: ${row.remoteAddress}`}
                        sx={{ 
                          color: '#ffa726', 
                          width: 140,
                          minWidth: 140,
                          maxWidth: 140,
                          fontFamily: 'monospace',
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {row.remoteAddress}
                      </Typography>
                      
                      {/* AI Analysis Button */}
                      <Box sx={{ width: 80, minWidth: 80, maxWidth: 80, textAlign: 'center' }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedEvent(row);
                            setModalOpen(true);
                          }}
                          sx={{
                            color: '#00b4a0',
                            backgroundColor: 'rgba(0,180,160,0.1)',
                            border: '1px solid rgba(0,180,160,0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(0,180,160,0.2)',
                              transform: 'scale(1.05)'
                            },
                            transition: 'all 0.2s'
                          }}
                          title="View AI Analysis"
                        >
                          <SmartToyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })
              )}
        </CardContent>

        {/* AI Analysis Modal */}
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Card sx={{
            maxWidth: 800,
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'rgba(10,34,64,0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0,180,160,0.3)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,180,160,0.2)',
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255,255,255,0.1)',
              borderRadius: '10px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'linear-gradient(180deg, #00b4a0 0%, #1e90ff 100%)',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.1)',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'linear-gradient(180deg, #00d4c0 0%, #4ea5ff 100%)',
              boxShadow: '0 0 10px rgba(0,180,160,0.5)',
            },
            // Firefox scrollbar styling
            scrollbarWidth: 'thin',
            scrollbarColor: '#00b4a0 rgba(255,255,255,0.1)'
          }}>
            <CardContent sx={{ p: 3 }}>
              {/* Modal Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ 
                  color: '#00b4a0', 
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <SmartToyIcon /> AI Security Analysis
                </Typography>
                <IconButton onClick={() => setModalOpen(false)} sx={{ color: '#b0bec5' }}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {selectedEvent && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  
                  {/* Basic Info Section */}
                  <Card sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#60a5fa', mb: 2, fontWeight: 600 }}>
                      Event Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#b0bec5' }}>Host</Typography>
                        <Typography variant="body2" sx={{ color: 'white', fontFamily: 'monospace' }}>
                          {selectedEvent.host}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#b0bec5' }}>Remote Address</Typography>
                        <Typography variant="body2" sx={{ color: '#ffa726', fontFamily: 'monospace' }}>
                          {selectedEvent.remoteAddress}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#b0bec5' }}>Threat Level</Typography>
                        <Typography variant="body2" sx={{ 
                          color: selectedEvent.levelNum <= 2 ? '#f44336' : 
                                selectedEvent.levelNum <= 4 ? '#ff9800' : 
                                selectedEvent.levelNum <= 5 ? '#2196f3' : '#4caf50',
                          fontWeight: 600
                        }}>
                          {selectedEvent.level}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Confidence Score - Full Width */}
                    {selectedEvent.confidenceScore !== 'N/A' && (
                      <Box sx={{ 
                        mt: 2, 
                        pt: 2, 
                        borderTop: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block', mb: 1.5 }}>
                          AI Confidence Score
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 2,
                          backgroundColor: 'rgba(233,30,99,0.1)',
                          px: 3,
                          py: 2,
                          borderRadius: 2,
                          border: '1px solid rgba(233,30,99,0.3)',
                          width: '100%'
                        }}>
                          <Typography variant="h4" sx={{ 
                            color: (() => {
                              const score = parseFloat(selectedEvent.confidenceScore);
                              const normalizedScore = score > 1 ? score / 100 : score;
                              return normalizedScore >= 0.8 ? '#4caf50' :
                                     normalizedScore >= 0.6 ? '#ff9800' :
                                     normalizedScore >= 0.4 ? '#ff5722' : '#f44336';
                            })(),
                            fontWeight: 700,
                            fontFamily: 'monospace',
                            minWidth: '80px'
                          }}>
                            {(() => {
                              const score = parseFloat(selectedEvent.confidenceScore);
                              const normalizedScore = score > 1 ? score : score * 100;
                              return normalizedScore.toFixed(0) + '%';
                            })()}
                          </Typography>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{
                              width: '100%',
                              height: 10,
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              borderRadius: 3,
                              overflow: 'hidden'
                            }}>
                              <Box sx={{
                                width: `${(() => {
                                  const score = parseFloat(selectedEvent.confidenceScore);
                                  const normalizedScore = score > 1 ? score : score * 100;
                                  return Math.min(100, Math.max(0, normalizedScore));
                                })()}%`,
                                height: '100%',
                                backgroundColor: (() => {
                                  const score = parseFloat(selectedEvent.confidenceScore);
                                  const normalizedScore = score > 1 ? score / 100 : score;
                                  return normalizedScore >= 0.8 ? '#4caf50' :
                                         normalizedScore >= 0.6 ? '#ff9800' :
                                         normalizedScore >= 0.4 ? '#ff5722' : '#f44336';
                                })(),
                                borderRadius: 3,
                                transition: 'width 0.3s ease'
                              }} />
                            </Box>
                            <Typography variant="body2" sx={{ 
                              color: '#b0bec5',
                              fontStyle: 'italic',
                              fontWeight: 500
                            }}>
                              {(() => {
                                const score = parseFloat(selectedEvent.confidenceScore);
                                const normalizedScore = score > 1 ? score / 100 : score;
                                return normalizedScore >= 0.8 ? 'High Confidence' :
                                       normalizedScore >= 0.6 ? 'Medium Confidence' :
                                       normalizedScore >= 0.4 ? 'Low Confidence' : 'Very Low Confidence';
                              })()}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Card>

                  {/* Raw Message */}
                  <Card sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#60a5fa', mb: 2, fontWeight: 600 }}>
                      Raw Log Message
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: 'white', 
                      fontFamily: 'monospace',
                      backgroundColor: 'rgba(0,0,0,0.3)',
                      p: 2,
                      borderRadius: 1,
                      wordBreak: 'break-word'
                    }}>
                      {selectedEvent.message}
                    </Typography>
                  </Card>

                  {/* AI Analysis Section */}
                  <Card sx={{ background: 'rgba(255,255,255,0.05)', borderRadius: 2, p: 2 }}>
                    <Typography variant="h6" sx={{ color: '#00b4a0', mb: 2, fontWeight: 600 }}>
                      AI Security Analysis
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Attack Pattern */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#ff6b6b', fontWeight: 600, mb: 1 }}>
                          Attack Pattern
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white' }}>
                          {selectedEvent.attackPattern}
                        </Typography>
                      </Box>

                      {/* Threat Category */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#ffa726', fontWeight: 600, mb: 1 }}>
                          Threat Category
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'white',
                          backgroundColor: 'rgba(255,167,38,0.2)',
                          display: 'inline-block',
                          px: 2,
                          py: 0.5,
                          borderRadius: 1
                        }}>
                          {selectedEvent.threatCategory}
                        </Typography>
                      </Box>

                      {/* Severity & AI Status */}
                      {(selectedEvent.severity !== 'N/A' || selectedEvent.aiStatus !== 'N/A') && (
                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          {selectedEvent.severity !== 'N/A' && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#f44336', fontWeight: 600, mb: 1 }}>
                                Severity
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                color: 'white',
                                backgroundColor: selectedEvent.severity === 'high' || selectedEvent.severity === 'critical' 
                                  ? 'rgba(244,67,54,0.2)' 
                                  : selectedEvent.severity === 'medium' 
                                  ? 'rgba(255,152,0,0.2)' 
                                  : 'rgba(76,175,80,0.2)',
                                display: 'inline-block',
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                                textTransform: 'uppercase',
                                fontWeight: 600
                              }}>
                                {selectedEvent.severity}
                              </Typography>
                            </Box>
                          )}
                          {selectedEvent.aiStatus !== 'N/A' && (
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#00b4a0', fontWeight: 600, mb: 1 }}>
                                AI Status
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                color: 'white',
                                backgroundColor: selectedEvent.aiStatus === true || selectedEvent.aiStatus === 'true'
                                  ? 'rgba(0,180,160,0.2)'
                                  : 'rgba(176,190,197,0.2)',
                                display: 'inline-block',
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 600
                              }}>
                                {selectedEvent.aiStatus === true || selectedEvent.aiStatus === 'true' ? '✓ Active' : '✗ Inactive'}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* Extracted IOCs (IPs) */}
                      {selectedEvent.extractedIOCs && selectedEvent.extractedIOCs.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#ff9800', fontWeight: 600, mb: 1 }}>
                            Extracted IOCs (IP Addresses)
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedEvent.extractedIOCs.map((ip, idx) => (
                              <Typography key={ip + idx} variant="body2" sx={{ 
                                color: '#ff9800',
                                fontFamily: 'monospace',
                                backgroundColor: 'rgba(255,152,0,0.2)',
                                display: 'inline-block',
                                px: 2,
                                py: 0.5,
                                borderRadius: 1,
                                fontWeight: 600
                              }}>
                                {ip}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}

                      {/* Explanation */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#4fc3f7', fontWeight: 600, mb: 1 }}>
                          Explanation
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                          {selectedEvent.explanation}
                        </Typography>
                      </Box>

                      {/* MITRE ATT&CK Technique */}
                      {selectedEvent.mitreAttackTechnique !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#ab47bc', fontWeight: 600, mb: 1 }}>
                            MITRE ATT&CK Technique
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            {selectedEvent.mitreAttackTechnique}
                          </Typography>
                        </Box>
                      )}

                      {/* Remediation Steps */}
                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#66bb6a', fontWeight: 600, mb: 1 }}>
                          Recommended Actions
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                          {selectedEvent.remediationSteps}
                        </Typography>
                      </Box>

                      {/* Similar Attacks */}
                      {selectedEvent.similarAttacks !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#ff8a65', fontWeight: 600, mb: 1 }}>
                            Similar Attack Patterns
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                            {selectedEvent.similarAttacks}
                          </Typography>
                        </Box>
                      )}

                      {/* Suspicious Traffic */}
                      {selectedEvent.suspiciousTraffic !== 'N/A' && selectedEvent.suspiciousTraffic !== null && selectedEvent.suspiciousTraffic !== undefined && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#ffb74d', fontWeight: 600, mb: 1 }}>
                            Traffic Analysis
                          </Typography>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            backgroundColor: selectedEvent.suspiciousTraffic === true || selectedEvent.suspiciousTraffic === 'true' 
                              ? 'rgba(239,68,68,0.2)' 
                              : 'rgba(74,222,128,0.2)',
                            border: selectedEvent.suspiciousTraffic === true || selectedEvent.suspiciousTraffic === 'true'
                              ? '1px solid rgba(239,68,68,0.5)'
                              : '1px solid rgba(74,222,128,0.5)'
                          }}>
                            <Typography variant="h6" sx={{ 
                              fontSize: '1.2rem',
                              color: selectedEvent.suspiciousTraffic === true || selectedEvent.suspiciousTraffic === 'true' 
                                ? '#f44336' 
                                : '#4ade80'
                            }}>
                              {selectedEvent.suspiciousTraffic === true || selectedEvent.suspiciousTraffic === 'true' ? '⚠️' : '✓'}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: 'white',
                              fontWeight: 600
                            }}>
                              {selectedEvent.suspiciousTraffic === true || selectedEvent.suspiciousTraffic === 'true' 
                                ? 'Suspicious Activity Detected' 
                                : 'No Suspicious Activity'}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {/* Kill Chain Stage */}
                      {selectedEvent.llm_kill_chain_stage !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#9c27b0', fontWeight: 600, mb: 1 }}>
                            Kill Chain Stage
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            {selectedEvent.llm_kill_chain_stage}
                          </Typography>
                        </Box>
                      )}

                      {/* Attack Narrative */}
                      {selectedEvent.llm_attack_narrative !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#e91e63', fontWeight: 600, mb: 1 }}>
                            Attack Narrative
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                            {selectedEvent.llm_attack_narrative}
                          </Typography>
                        </Box>
                      )}

                      {/* Decoded Commands */}
                      {selectedEvent.llm_decoded_commands !== 'N/A' && selectedEvent.llm_decoded_commands !== '' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#ff5722', fontWeight: 600, mb: 1 }}>
                            Decoded Commands
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'white',
                            fontFamily: 'monospace',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            p: 1.5,
                            borderRadius: 1
                          }}>
                            {selectedEvent.llm_decoded_commands}
                          </Typography>
                        </Box>
                      )}

                      {/* Data Classification */}
                      {selectedEvent.llm_data_classification !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#3f51b5', fontWeight: 600, mb: 1 }}>
                            Data Classification
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white' }}>
                            {selectedEvent.llm_data_classification}
                          </Typography>
                        </Box>
                      )}

                      {/* User Risk Score */}
                      {selectedEvent.llm_user_risk_score !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#ff9800', fontWeight: 600, mb: 1 }}>
                            User Risk Score
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h5" sx={{ 
                              color: (() => {
                                const score = parseFloat(selectedEvent.llm_user_risk_score);
                                return score >= 75 ? '#f44336' : score >= 50 ? '#ff9800' : score >= 25 ? '#ffc107' : '#4caf50';
                              })(),
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              minWidth: '60px'
                            }}>
                              {selectedEvent.llm_user_risk_score}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{
                                width: '100%',
                                height: 8,
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderRadius: 2,
                                overflow: 'hidden'
                              }}>
                                <Box sx={{
                                  width: `${Math.min(100, Math.max(0, parseFloat(selectedEvent.llm_user_risk_score)))}%`,
                                  height: '100%',
                                  backgroundColor: (() => {
                                    const score = parseFloat(selectedEvent.llm_user_risk_score);
                                    return score >= 75 ? '#f44336' : score >= 50 ? '#ff9800' : score >= 25 ? '#ffc107' : '#4caf50';
                                  })(),
                                  borderRadius: 2,
                                  transition: 'width 0.3s ease'
                                }} />
                              </Box>
                              <Typography variant="caption" sx={{ color: '#b0bec5', mt: 0.5, display: 'block' }}>
                                {(() => {
                                  const score = parseFloat(selectedEvent.llm_user_risk_score);
                                  return score >= 75 ? 'Critical Risk - Immediate attention required' : 
                                         score >= 50 ? 'High Risk - Investigation recommended' : 
                                         score >= 25 ? 'Medium Risk - Monitor activity' : 'Low Risk - Normal activity';
                                })()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {/* Asset Criticality */}
                      {selectedEvent.llm_asset_criticality !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#f44336', fontWeight: 600, mb: 1 }}>
                            Asset Criticality
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: 'white',
                            backgroundColor: 'rgba(244,67,54,0.2)',
                            display: 'inline-block',
                            px: 2,
                            py: 0.5,
                            borderRadius: 1,
                            textTransform: 'uppercase',
                            fontWeight: 600
                          }}>
                            {selectedEvent.llm_asset_criticality}
                          </Typography>
                        </Box>
                      )}

                      {/* False Positive Likelihood */}
                      {selectedEvent.llm_false_positive_likelihood !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#00bcd4', fontWeight: 600, mb: 1 }}>
                            False Positive Likelihood
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h5" sx={{ 
                              color: (() => {
                                const score = parseFloat(selectedEvent.llm_false_positive_likelihood) * 100;
                                return score >= 75 ? '#4caf50' : score >= 50 ? '#ffc107' : score >= 25 ? '#ff9800' : '#f44336';
                              })(),
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              minWidth: '60px'
                            }}>
                              {(parseFloat(selectedEvent.llm_false_positive_likelihood) * 100).toFixed(0)}%
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{
                                width: '100%',
                                height: 8,
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderRadius: 2,
                                overflow: 'hidden'
                              }}>
                                <Box sx={{
                                  width: `${Math.min(100, Math.max(0, parseFloat(selectedEvent.llm_false_positive_likelihood) * 100))}%`,
                                  height: '100%',
                                  backgroundColor: (() => {
                                    const score = parseFloat(selectedEvent.llm_false_positive_likelihood) * 100;
                                    return score >= 75 ? '#4caf50' : score >= 50 ? '#ffc107' : score >= 25 ? '#ff9800' : '#f44336';
                                  })(),
                                  borderRadius: 2,
                                  transition: 'width 0.3s ease'
                                }} />
                              </Box>
                              <Typography variant="caption" sx={{ color: '#b0bec5', mt: 0.5, display: 'block' }}>
                                {(() => {
                                  const score = parseFloat(selectedEvent.llm_false_positive_likelihood) * 100;
                                  return score >= 75 ? 'Likely False Positive - Low priority' : 
                                         score >= 50 ? 'Possibly False Positive - Review carefully' : 
                                         score >= 25 ? 'Probably Genuine - Investigate' : 'Genuine Threat - High confidence';
                                })()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {/* Context Window */}
                      {selectedEvent.llm_context_window !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#673ab7', fontWeight: 600, mb: 1 }}>
                            Context Window
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                            {selectedEvent.llm_context_window}
                          </Typography>
                        </Box>
                      )}

                      {/* Anomaly Score */}
                      {selectedEvent.llm_anomaly_score !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#ffc107', fontWeight: 600, mb: 1 }}>
                            Anomaly Score
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h5" sx={{ 
                              color: (() => {
                                const score = parseFloat(selectedEvent.llm_anomaly_score);
                                return score >= 75 ? '#f44336' : score >= 50 ? '#ff9800' : score >= 25 ? '#ffc107' : '#4caf50';
                              })(),
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              minWidth: '60px'
                            }}>
                              {selectedEvent.llm_anomaly_score}
                            </Typography>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{
                                width: '100%',
                                height: 8,
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderRadius: 2,
                                overflow: 'hidden'
                              }}>
                                <Box sx={{
                                  width: `${Math.min(100, Math.max(0, parseFloat(selectedEvent.llm_anomaly_score)))}%`,
                                  height: '100%',
                                  backgroundColor: (() => {
                                    const score = parseFloat(selectedEvent.llm_anomaly_score);
                                    return score >= 75 ? '#f44336' : score >= 50 ? '#ff9800' : score >= 25 ? '#ffc107' : '#4caf50';
                                  })(),
                                  borderRadius: 2,
                                  transition: 'width 0.3s ease'
                                }} />
                              </Box>
                              <Typography variant="caption" sx={{ color: '#b0bec5', mt: 0.5, display: 'block' }}>
                                {(() => {
                                  const score = parseFloat(selectedEvent.llm_anomaly_score);
                                  return score >= 75 ? 'Highly Anomalous - Unusual behavior detected' : 
                                         score >= 50 ? 'Moderately Anomalous - Investigate further' : 
                                         score >= 25 ? 'Slightly Anomalous - Minor deviation' : 'Normal - Expected behavior';
                                })()}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {/* Recommended Actions */}
                      {selectedEvent.llm_recommended_actions !== 'N/A' && (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#4caf50', fontWeight: 600, mb: 1 }}>
                            Recommended Actions
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'white', lineHeight: 1.6 }}>
                            {selectedEvent.llm_recommended_actions}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Card>
                </Box>
              )}
            </CardContent>
          </Card>
        </Modal>
    </>
  )
}

export default LiveEventPath3Splunk